import { useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
  gravity: number;
  friction: number;
  trailLength: number;
  history: { x: number; y: number }[];
  shimmer: number;
  bounces: number;
  maxBounces: number;
  landed: boolean;
  landedLife: number;
  ember: boolean;
  emberMaxLife: number;
  twinkleSpeed: number;
  twinklePhase: number;
}

interface Rocket {
  x: number;
  y: number;
  targetY: number;
  vx: number;
  vy: number;
  color: string;
  trail: { x: number; y: number; alpha: number }[];
  exploded: boolean;
}

const PALETTES = [
  // Cool aurora
  ["#7ee8fa", "#eec0c6", "#ffffff", "#b3e0ff", "#ffd6f6"],
  // Mint pastel
  ["#a1ffce", "#faffd1", "#ffffff", "#c2f0fc", "#e0c3fc"],
  // Warm sunset
  ["#f9d423", "#ff6a3d", "#ffffff", "#fceabb", "#fdd692"],
  // Candy pop
  ["#ff9a9e", "#fecfef", "#ffffff", "#fbc2eb", "#f6d365"],
  // Neon dream
  ["#89f7fe", "#66a6ff", "#ffffff", "#a1c4fd", "#c471f5"],
  // Rose gold
  ["#ffcfdf", "#fefdca", "#ffffff", "#ffd3a5", "#fd6585"],
  // Emerald / gold
  ["#43e97b", "#38f9d7", "#ffffff", "#fceabb", "#fddb92"],
];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export default function FireworksCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const rockets: Rocket[] = [];
    const particles: Particle[] = [];

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      width = parent.clientWidth;
      height = parent.clientHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);

    const launchRocket = (originX?: number) => {
      const startX = originX ?? Math.random() * width * 0.8 + width * 0.1;
      // Explode at ~2/3 up from the bottom => targetY sits at ~1/3 from the top
      const targetY = height * 0.28 + Math.random() * height * 0.12;
      const startY = height + 10;
      const travel = startY - targetY;
      const duration = 130 + Math.random() * 40;
      const vy = -travel / duration;
      const vx = (Math.random() - 0.5) * 0.4;
      const palette = pick(PALETTES);
      rockets.push({
        x: startX,
        y: startY,
        targetY,
        vx,
        vy,
        color: pick(palette),
        trail: [],
        exploded: false,
      });
    };

    const spawnBurst = (
      cx: number,
      cy: number,
      palette: string[],
      count: number,
      baseSpeed: number,
      speedJitter: number,
      style: "sphere" | "willow" | "ring" | "chrysanthemum",
      sizeMul = 1
    ) => {
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.08;
        let speed = baseSpeed * (1 - speedJitter + Math.random() * speedJitter * 2);

        // Ring: uniform speed; Sphere: 3D projection; Willow: droopy; Chrysanthemum: normal radial
        if (style === "ring") {
          speed = baseSpeed;
        } else if (style === "sphere") {
          // simulate 3D by scaling speed with sin(random polar angle)
          const polar = Math.acos(2 * Math.random() - 1);
          speed = baseSpeed * Math.sin(polar) * (0.85 + Math.random() * 0.3);
        }

        let vx = Math.cos(angle) * speed;
        let vy = Math.sin(angle) * speed;

        if (style === "willow") {
          vx *= 0.55;
          vy = vy * 0.35 + 0.9;
        }

        const color = pick(palette);
        particles.push({
          x: cx,
          y: cy,
          vx,
          vy,
          life: 0,
          maxLife: 220 + Math.random() * 160,
          color,
          size: (1.6 + Math.random() * 1.8) * sizeMul,
          gravity: 0.028 + Math.random() * 0.022,
          friction: 0.988,
          trailLength: 8 + Math.floor(Math.random() * 10),
          history: [],
          shimmer: Math.random() * Math.PI * 2,
          bounces: 0,
          maxBounces: 2 + Math.floor(Math.random() * 2),
          landed: false,
          landedLife: 0,
          ember: Math.random() < 0.4,
          emberMaxLife: 500 + Math.random() * 700,
          twinkleSpeed: 0.08 + Math.random() * 0.18,
          twinklePhase: Math.random() * Math.PI * 2,
        });
      }
    };

    const explode = (rocket: Rocket) => {
      const palette = pick(PALETTES);
      const secondaryPalette = pick(PALETTES);
      const styleRoll = Math.random();

      // Bright core flash
      for (let i = 0; i < 30; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 9 + 2;
        particles.push({
          x: rocket.x,
          y: rocket.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0,
          maxLife: 26 + Math.random() * 20,
          color: "#ffffff",
          size: 2.4 + Math.random() * 2,
          gravity: 0.02,
          friction: 0.9,
          trailLength: 5,
          history: [],
          shimmer: 0,
          bounces: 0,
          maxBounces: 0,
          landed: false,
          landedLife: 0,
          ember: false,
          emberMaxLife: 0,
          twinkleSpeed: 0,
          twinklePhase: 0,
        });
      }

      if (styleRoll < 0.28) {
        // Double-layer chrysanthemum (outer big + inner smaller ring)
        spawnBurst(rocket.x, rocket.y, palette, 130, 6.0, 0.35, "chrysanthemum");
        spawnBurst(rocket.x, rocket.y, secondaryPalette, 70, 3.2, 0.3, "ring", 0.85);
      } else if (styleRoll < 0.5) {
        // Spherical burst (3D-looking dandelion)
        spawnBurst(rocket.x, rocket.y, palette, 180, 6.5, 0.25, "sphere");
        spawnBurst(rocket.x, rocket.y, secondaryPalette, 40, 2.6, 0.4, "chrysanthemum", 0.7);
      } else if (styleRoll < 0.72) {
        // Willow / weeping
        spawnBurst(rocket.x, rocket.y, palette, 140, 5.2, 0.4, "willow");
        spawnBurst(rocket.x, rocket.y, secondaryPalette, 50, 2.4, 0.35, "chrysanthemum", 0.8);
      } else if (styleRoll < 0.88) {
        // Perfect ring + core burst
        spawnBurst(rocket.x, rocket.y, palette, 90, 5.5, 0.05, "ring");
        spawnBurst(rocket.x, rocket.y, secondaryPalette, 60, 2.8, 0.4, "chrysanthemum", 0.75);
      } else {
        // Grand finale: sphere + ring + chrysanthemum layers
        spawnBurst(rocket.x, rocket.y, palette, 150, 6.2, 0.3, "sphere");
        spawnBurst(rocket.x, rocket.y, secondaryPalette, 80, 4.5, 0.05, "ring", 0.9);
        spawnBurst(rocket.x, rocket.y, palette, 40, 2.2, 0.4, "chrysanthemum", 0.7);
      }

      // Hard cap total particles to avoid runaway lag
      const MAX_PARTICLES = 1400;
      if (particles.length > MAX_PARTICLES) {
        particles.splice(0, particles.length - MAX_PARTICLES);
      }
    };

    // Initial burst schedule
    let lastLaunch = 0;
    let launchInterval = 1200;

    // Kick off one immediately
    launchRocket(width * 0.5);

    const render = (t: number) => {
      // Trail fade
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "rgba(4, 12, 46, 0.22)";
      ctx.fillRect(0, 0, width, height);

      // Additive blending for glow
      ctx.globalCompositeOperation = "lighter";

      // Launch new rockets on interval
      if (t - lastLaunch > launchInterval) {
        launchRocket();
        lastLaunch = t;
        launchInterval = 1100 + Math.random() * 1300;
        // Occasional simultaneous launches for dramatic feel
        if (Math.random() < 0.25) {
          setTimeout(() => launchRocket(), 300 + Math.random() * 500);
        }
        if (Math.random() < 0.1) {
          setTimeout(() => launchRocket(), 600 + Math.random() * 400);
        }
      }

      // Update & draw rockets
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.x += r.vx;
        r.y += r.vy;
        r.vy *= 0.995;
        r.trail.push({ x: r.x, y: r.y, alpha: 1 });
        if (r.trail.length > 20) r.trail.shift();

        // Draw trail
        for (let j = 0; j < r.trail.length; j++) {
          const p = r.trail[j];
          const alpha = (j / r.trail.length) * 0.85;
          ctx.beginPath();
          ctx.fillStyle = `rgba(255,240,200,${alpha})`;
          ctx.arc(p.x, p.y, 1.8, 0, Math.PI * 2);
          ctx.fill();
        }

        // Draw rocket head (bright)
        ctx.beginPath();
        const grad = ctx.createRadialGradient(r.x, r.y, 0, r.x, r.y, 10);
        grad.addColorStop(0, "rgba(255,255,255,1)");
        grad.addColorStop(0.4, "rgba(255,220,150,0.9)");
        grad.addColorStop(1, "rgba(255,180,80,0)");
        ctx.fillStyle = grad;
        ctx.arc(r.x, r.y, 10, 0, Math.PI * 2);
        ctx.fill();

        if (r.y <= r.targetY) {
          explode(r);
          rockets.splice(i, 1);
        }
      }

      // Update & draw particles (no bouncing — particles simply fade and fall away)
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.history.push({ x: p.x, y: p.y });
        if (p.history.length > p.trailLength) p.history.shift();

        p.vx *= p.friction;
        p.vy *= p.friction;
        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;

        p.life++;
        p.shimmer += 0.35;

        const lifeRatio = 1 - p.life / p.maxLife;

        if (lifeRatio <= 0 || p.y > height + 20 || p.x < -20 || p.x > width + 20) {
          particles.splice(i, 1);
          continue;
        }

        // Draw trail (sparkling)
        for (let j = 0; j < p.history.length; j++) {
          const hp = p.history[j];
          const a = (j / p.history.length) * lifeRatio * 0.75;
          ctx.beginPath();
          ctx.fillStyle = hexToRgba(p.color, a);
          const size = p.size * (j / p.history.length) * 0.95;
          ctx.arc(hp.x, hp.y, size, 0, Math.PI * 2);
          ctx.fill();
        }

        // Draw head with shimmer
        const shimmer = 0.65 + Math.sin(p.shimmer) * 0.35;
        ctx.beginPath();
        ctx.fillStyle = hexToRgba(p.color, lifeRatio * shimmer);
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        // Bright core
        ctx.beginPath();
        ctx.fillStyle = `rgba(255,255,255,${lifeRatio * shimmer * 0.65})`;
        ctx.arc(p.x, p.y, p.size * 0.45, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = "source-over";
      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    // Click to launch a rocket at click position
    const handleClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      launchRocket(x);
    };
    canvas.addEventListener("click", handleClick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("click", handleClick);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full cursor-pointer"
      aria-hidden="true"
    />
  );
}

function hexToRgba(hex: string, alpha: number): string {
  const cleaned = hex.replace("#", "");
  const full =
    cleaned.length === 3
      ? cleaned
          .split("")
          .map((c) => c + c)
          .join("")
      : cleaned;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}